import React from 'react'
import { useHistory } from 'react-router-dom'
import { zodResolver as hookformZodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import type { UseFormReturn } from 'react-hook-form'
import { convertDashboardCardDefToDashboardCardConfig } from './converters'
import { objectDiffByPath } from './utils/objectDiffByPath'
import { syncAqlAndFormValues } from './converters/utils/syncAqlAndFormValues'
import { zodSchema } from './zodSchema'
import { errorToRoute } from './TabViewer'
import type { DashboardCardConfig } from './types'
import type { DashboardCardDef } from '@edge/spark/io-dashboards'
import { useIsCardEditbleByUser } from './ConfigurationModal/SearchCardConfigurationModal.utils'

export const useCardConfigForm = (widgetDef: DashboardCardDef): UseFormReturn<DashboardCardConfig, any> => {
    const cardConfiguratorHistory = useHistory()
    const allowEditing = useIsCardEditbleByUser(widgetDef)
    const defaultValues = React.useMemo(
        () => convertDashboardCardDefToDashboardCardConfig(widgetDef, !allowEditing),
        [widgetDef],
    )

    const methods = useForm<DashboardCardConfig>({
        shouldUnregister: false,
        defaultValues,
        mode: 'onBlur',
        reValidateMode: 'onBlur',
        resolver: async (preValidationValues, context, options) => {
            // Attempt to preempt any validation for non-custom cards. e.g. Overview, Needs Photos, etc...
            if (preValidationValues.type === 'STATIC' || (!options.names?.length && !preValidationValues.aql)) {
                return { values: preValidationValues, errors: {} }
            }

            // Perform validation and any transformations directly from form values
            const { values: postValidationValues, errors: postValidationErrors } = await hookformZodResolver(zodSchema)(
                preValidationValues,
                context,
                options,
            )

            // Route to specific page of error
            if (Object.keys(postValidationErrors).length) {
                const route = errorToRoute(postValidationErrors)
                if (route) {
                    cardConfiguratorHistory.push(route)
                }

                return { values: postValidationValues, errors: postValidationErrors }
            }

            // Handle possible empty object from TypeScript?
            if (!('filters' in postValidationValues)) {
                return { values: postValidationValues, errors: {} }
            }

            // Sync AQL with filters after validation
            const syncedConfig = syncAqlAndFormValues(postValidationValues, options.names)

            // Update the form state with the changes from validation transformations and sync
            const diff = objectDiffByPath(preValidationValues, syncedConfig)

            // Disabled because objectDiffByPath/deep-object-diff doesn't have generic types
            // `diff` looks something like: `{ 'aql': 'color:red', 'format.cardTitle': 'hello world' }`
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            Object.entries(diff).forEach(([key, value]) => methods.resetField(key as any, { defaultValue: value }))

            return { values: postValidationValues, errors: postValidationErrors }
        },
    })

    return methods
}

export default useCardConfigForm
